import { ACCOUNT_ROLES, TEAM_ROLES } from "./articleRoles";

const CATEGORY = "Team and roles";

export const TEAM_ARTICLES = [
   {
      id: "roles-and-permissions",
      category: CATEGORY,
      title: "Roles and permissions",
      summary: "What each role can see and do in KAPsurvey.",
      related: ["organization-members", "project-team"],
      body: `
Every person in an organization has **one role** in it. The role decides which pages and buttons you see. If you belong to several organizations, you can have a different role in each.

Within a role, you only see the workspaces and projects you've been added to.

## The roles

- **Organization**: the account owner. Can do everything, including billing and adding people to the organization.
- **Senior Manager**: oversees several projects. Manages workspaces, projects, surveys and members, but not billing.
- **Project Manager**: runs the projects they belong to: surveys, audiences, campaigns, assignments and the project team.
- **Junior Manager**: supports daily data collection. Views surveys and results, exports data, works with audiences and assigns surveys to data collectors.
- **Data Collector**: collects data. Sees only the surveys assigned to them on **My tasks**.
- **Guest**: view-only access to the projects they are added to.

## Who can do what

| Action | Who can do it |
|---|---|
| Manage the subscription, usage and balance | Organization |
| Add people to the organization | Organization |
| Edit or remove people in the organization | Organization, Senior Manager |
| Create, edit and delete workspaces | Organization, Senior Manager |
| Create, edit and delete projects | Organization, Senior Manager, Project Manager |
| Create, edit, publish and delete surveys | Organization, Senior Manager, Project Manager |
| Send surveys through campaigns | Organization, Senior Manager, Project Manager |
| Import or delete audiences, delete submissions, restore deleted items | Organization, Senior Manager, Project Manager |
| View, edit and export audiences | All managers |
| Assign surveys to data collectors | All managers |
| Add data collectors to a project | All managers |
| Open the data table and export submissions | All managers |
| View surveys, the project dashboard and results | All managers, Guest |
| Fill in assigned surveys | Data Collector |
| Open support tickets | Everyone except Data Collector |

"All managers" means the Organization, Senior Managers, Project Managers and Junior Managers.

## Which roles you can give

- **To the organization** (the **Team** page): the Organization can add Senior Managers, Project Managers and Guests. See [Manage your organization's members](#organization-members).
- **To a project** (the project's **Team** page): Senior Managers can add Project Managers, Junior Managers and Data Collectors. The Organization and Project Managers can add Junior Managers and Data Collectors. Junior Managers can add Data Collectors. See [Add people to a project](#project-team).

Nobody can change their own role.
`
   },
   {
      id: "organization-members",
      category: CATEGORY,
      title: "Manage your organization's members",
      summary: "Add people to your organization, edit their details and remove them.",
      roles: TEAM_ROLES,
      related: ["roles-and-permissions", "project-team"],
      body: `
The **Team** page lists everyone in your organization. Click your avatar and choose **Team** to open it.

The Organization adds people. The Organization and Senior Managers can edit and remove them.

## Add a member

1. Click **Add member**.
2. On the **Add one member** tab, fill in **Full name**, **Email**, **Language**, **Gender** and **Location**.
3. Choose the workspaces the person works in under **Select Workspaces**.
4. Choose a role under **Select Role**: Senior Manager, Project Manager or Guest. See [Roles and permissions](#roles-and-permissions).
5. Click **Add new member**.

Each person can have one role per organization, and each email address can be used only once.

> **Tip:** Junior Managers and Data Collectors are added to a project instead. See [Add people to a project](#project-team).

## Add several members at once

1. Click **Add member** and open the **Add multiple members** tab.
2. Click **Download Format** and fill in the template, one row per person.
3. Drop the file on the box. Excel files only, up to 1 MB.
4. Choose the workspaces under **Select workspaces:** and click **Create Profile**.

Rows with a role, language or location the template doesn't list are skipped. Use the values from the template's lists.

## Find, view and edit members

- Type in **Search...**, or click **Filters** to filter by role, language, gender, location, workspace or project. **Clear filters** resets them.
- Click the eye icon to view a member, or the pencil icon to change their details, workspaces or role.

## Remove members

1. Click the bin icon on a member's row, or tick several rows and click **Delete selected rows**.
2. Confirm with **Yes, delete it**.

Removed members are kept under **Deleted items**, on the **Users** tab, where you can restore them.
`
   },
   {
      id: "project-team",
      category: CATEGORY,
      title: "Add people to a project",
      summary: "Give data collectors, junior managers and project managers access to one project.",
      roles: ACCOUNT_ROLES,
      related: ["roles-and-permissions", "organization-members"],
      body: `
Each project has its own team. Adding someone here gives them access to the current workspace and project.

## Add someone to the project

1. Open the project, then choose **Team** under **Project Settings** in the sidebar.
2. Click **Add member**.
3. Fill in the person's details and choose a role. Which roles you can give depends on your own role:
   - **Senior Manager**: Project Manager, Junior Manager or Data Collector.
   - **Organization** or **Project Manager**: Junior Manager or Data Collector.
   - **Junior Manager**: Data Collector.
4. Click **Add new member**.

> **Tip:** Only people with the Data Collector role can be assigned surveys to collect. See [Assign a survey to data collectors](#assign-data-collection).

## Edit or remove project members

The Organization, Senior Managers and Project Managers can edit or remove project members.

Removing someone here only removes them from **this project**. To remove a person from the whole organization, use the **Team** page in your avatar menu. See [Manage your organization's members](#organization-members).
`
   }
];
